import { z } from "zod";
import { DomainModelSchema } from "../types";
import { ProductType } from "@/constants/ProductType";
import { TankTechnicalDetailsSchema } from "../tankTechnicalDetails/types";
import { TankPlanSchemaVersion } from "@/constants/SchemaVersion";

/**
 * The possible states of the plan.
 */
export enum PlanStatus {
  Active = "Active",
  Complete = "Complete",
  Abandoned = "Abandoned",
}

// SEE SUP-804 for context. Basically a big flag for
// legacy data that doesn't use the global voyage object.
export enum PlanSchemaVersion {
  /// <summary>
  /// The plan has no reference to a voyage or tanks.
  /// </summary>
  Initial = "Initial",

  /// <summary>
  /// The plan  has a direct reference to a voyage, and doesn't need CoQs.
  /// </summary>
  PostProductQuality = "PostProductQuality",
}

/**
 * A record returned from a list plan request, contains a subset of the actual plan data.
 */
export const ListPlanModelSchema = z.object({
  id: z.number(),
  vessel: z.string().nullable(),
  voyage: z.string().nullable(),
  pilotOnBoard: z.coerce.date(),
  terminalId: z.number().int(),
  planStatus: z.nativeEnum(PlanStatus),
  schemaVersion: z.nativeEnum(PlanSchemaVersion),
});

export type ListPlanModel = z.infer<typeof ListPlanModelSchema>;

export const TankPlanModelSchema = z.intersection(
  DomainModelSchema.extend({
    name: z.string(),
    plannedReceipt: z.number(),
    receiptPlanId: z.number(),
    product: z.nativeEnum(ProductType),
    openingBalance: z.number(),
    estimated: z.number(),
    density: z.number(),
    pipelineAdjustments: z.number(),
    tankAdjustments: z.number(),
    productAdjustments: z.number(),
    adjustmentComments: z.string().nullable(),
    flashPoint: z.number().nullable(),
    conductivity: z.number().nullable(),
    viscosity: z.number().nullable(),
    cloudPoint: z.number().nullable(),
    coldFilterPluggingPoint: z.number().nullable(),
    pourPoint: z.number().nullable(),
    sulphur: z.number().nullable(),
    preDischargeLitres: z.number().nullable(),
    preDischargeMillimetres: z.number().nullable(),
    postDischargeLitres: z.number().nullable(),
    postDischargeMillimetres: z.number().nullable(),
    schemaVersion: z.nativeEnum(TankPlanSchemaVersion),
  }),
  TankTechnicalDetailsSchema,
);

export type TankPlanModel = z.infer<typeof TankPlanModelSchema>;

export const ReceiptPlanModelSchemaV1 = DomainModelSchema.extend({
  planId: z.number(),
  amountInKiloLitres: z.number(),
  product: z.nativeEnum(ProductType),
  balance: z.number(),
  tankPlans: z.record(z.string(), TankPlanModelSchema),
});

export type ReceiptPlanModelV1 = z.infer<typeof ReceiptPlanModelSchemaV1>;

/**
 * The full data of the plan, returned from a get request for this 'plan id'
 */
export const PlanModelSchemaV1 = DomainModelSchema.extend({
  vessel: z.string(),
  voyage: z.string(),
  pilotOnBoard: z.coerce.date(),
  terminalId: z.number().int(),
  terminalName: z.string(),
  terminalBerthName: z.string(),
  receiptPlans: z.record(z.string(), ReceiptPlanModelSchemaV1),
  planStatus: z.nativeEnum(PlanStatus),
  imoNumber: z.string().nullable(),
  schemaVersion: z.nativeEnum(PlanSchemaVersion),
});

export type PlanModelV1 = z.infer<typeof PlanModelSchemaV1>;

export const PlanModelSchemaV2 = DomainModelSchema.extend({
  voyageId: z.number(),
  pilotOnBoard: z.coerce.date(),
  terminalId: z.number().int(),
  terminalName: z.string(),
  terminalBerthName: z.string(),
  planStatus: z.nativeEnum(PlanStatus),
  imoNumber: z.string().nullable(),
  schemaVersion: z.nativeEnum(PlanSchemaVersion),
});

export type PlanModelV2 = z.infer<typeof PlanModelSchemaV2>;

export const UpdatePlanModelSchema = z.object({
  id: z.number().optional(),
  terminalId: z.number(),
  vessel: z.string(),
  voyage: z.string(),
  pilotOnBoard: z.date(),
  receiptPlans: z.record(z.string(), ReceiptPlanModelSchemaV1),
  planStatus: z.nativeEnum(PlanStatus),
  imoNumber: z.string().nullable(),
});

export type UpdatePlanModel = z.infer<typeof UpdatePlanModelSchema>;

export const NewPlanModelSchemaV1 = z.object({
  terminalId: z.number(),
  vessel: z.string(),
  voyage: z.string(),
  pilotOnBoard: z.date(),
});

export type NewPlanModelV1 = z.infer<typeof NewPlanModelSchemaV1>;

export const NewPlanModelSchemaV2 = z.object({
  terminalId: z.number(),
  voyageId: z.number().nullable(),
  pilotOnBoard: z.date(),
});

export type NewPlanModelV2 = z.infer<typeof NewPlanModelSchemaV2>;

export const UpdatePlanSchemaV2 = z.object({
  id: z.number(),
  terminalId: z.number(),
  voyageId: z.number().nullable(),
  pilotOnBoard: z.date(),
});

export type UpdatePlanV2 = z.infer<typeof UpdatePlanSchemaV2>;
